/**
 * Is confirming this field evidence the READER got it right, or the operator's own answer?
 *
 * The distinction is the whole value of the field ledger. `field-value-verified`
 * means the reader produced this value and a human vouched for it;
 * `field-value-correction` means the reader was wrong and here is the answer.
 * The miners read one and not the other, and "learn the corrections themselves" —
 * an operator repeatedly turning `STARK INDUSTRIES INC` into `Stark Industries` is
 * describing an alias — is the highest-frequency signal the product has and the
 * next learning rung to build. Mislabelling is not a cosmetic fault: it is the
 * training set.
 *
 * It has been got wrong once, expensively. Confirming a document confirmed every
 * field on it as `verified`, including ones the operator had just typed, so values
 * the reader never produced were filed as evidence the reader was right. On the
 * real job that cost **twenty-one of twenty-two corrections**. The observation
 * ledger knew — it keeps the prediction beside the answer, and they differ — but
 * the event stream said "verified", and the event stream is what the miners read.
 * The strongest signal in the product, filed as its opposite.
 *
 * That was fixed on 2026-07-29 at the confirm-document site by checking the field's
 * source. The rule it stated is general:
 *
 *   > A value carrying an operator source is the operator's, whatever button
 *   > confirmed it.
 *
 * It was applied at one of three sites. This module is that rule written once,
 * because the other two disagreed with it:
 *
 *  - `persistFieldValue` compared strings only. An operator who typed a value and
 *    then re-saved it unchanged — a second blur, or the detached fields panel
 *    sending the same edit — produced `field-value-verified` for their own value.
 *    `ApplyFieldValueVerification` UPDATEs, so that DOWNGRADES the observation's
 *    `verification_source` from correction to verification. An idempotent re-save
 *    silently deleted a correction from the training set.
 *  - The break-sheet pass compared the sheet's value to the field's, and it runs
 *    again over the whole merged batch on every append. On the second pass the
 *    field already HOLDS the sheet's value, so they match and it recorded
 *    `verified` — a value a human wrote on a cover sheet, filed as a reader hit.
 *
 * Neither loses data outright, because `predicted_value` still sits beside
 * `verified_value` and a miner comparing those two can still see the disagreement.
 * The point is that the label is what `verification_source` is FOR, and a label
 * that is wrong in one direction only will bias whatever is built on it.
 */

/** Only what the rule reads. Structural, so `FieldResult` and the engine's row both fit. */
export type CorrectionSubject = {
  value?: string | null;
  source?: string | null;
};

/**
 * Did a human author this value, rather than the reader produce it?
 *
 * `operator` and `operator OCR selection` are typed or clicked by the person at the
 * screen. `break-sheet` is a value a human wrote on a cover sheet before scanning,
 * which is the same claim arriving by a different road.
 *
 * `lookup` is deliberately NOT here. A value filled from the operator's own data
 * table is a machine prediction — a different machine than the reader, but still a
 * proposal a human can meaningfully vouch for, and the ledger's `predicted_value`
 * records what the reader said regardless. Excluding it is a judgement, so it is
 * written down rather than left to be inferred from the list.
 */
export const valueIsHumanAuthored = (field: CorrectionSubject | null | undefined): boolean => {
  const source = (field?.source ?? '').toLowerCase();
  return source.startsWith('operator') || source.startsWith('break-sheet');
};

/**
 * `field-value-verified` only when the reader produced exactly this value.
 *
 * Three ways to be a correction, and the order matters only for readability — any
 * one of them is sufficient:
 *
 *   1. There was no previous field at all. The reader produced nothing, so there is
 *      nothing it could have got right.
 *   2. The previous value was human-authored. Confirming it again confirms a human,
 *      not the reader — whatever button did the confirming.
 *   3. The value changed.
 *
 * Whitespace and case are NOT normalised before comparing. An operator who retypes
 * `ACME CORP` as `Acme Corp` has changed the value, and that change is exactly the
 * alias signal the next miner is meant to find. Folding it away here would delete
 * the thing being collected.
 */
export const fieldCorrectionIsCorrection = (
  before: CorrectionSubject | null | undefined,
  nextValue: string,
): boolean => !before || valueIsHumanAuthored(before) || before.value !== nextValue;
